import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotifyState } from '@shared/types';
import { NOTIFY_MODE_LABELS, type NotifyMode } from '@shared/notify';
import { formatDateTime } from '@shared/dates';
import { api, currentTenant } from '../api';
import { errorText, useToast } from '../store';
import { currentSubscription, pushSupport, subscribe, SUPPORT_HINT, unsubscribe, type PushSupport } from '../push';
import { Icons, Spinner } from './ui';

const permission = () => (typeof Notification === 'undefined' ? 'default' : Notification.permission);

/**
 * The member's notifications on this device. "On" means all three agree: the browser allows notifications, this device
 * has a subscription, and the server has it on file for this member — so a pressed bell is a confirmed one.
 */
export function useNotifications() {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['notify', currentTenant()];
  const { data: state } = useQuery({ queryKey: key, queryFn: api.notifications, staleTime: 60_000 });
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [support, setSupport] = useState<PushSupport>(pushSupport);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setEndpoint(s?.endpoint ?? null)).catch(() => setEndpoint(null)); }, []);
  const on = support === 'ok' && permission() === 'granted' && !!endpoint && !!state?.devices.some((d) => d.endpoint === endpoint);
  const put = (s: NotifyState) => qc.setQueryData(key, s);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast(errorText(e), 'error'); } finally { setBusy(false); setSupport(pushSupport()); }
  };

  const test = () => run(async () => {
    const r = await api.testNotification();
    put(r.state);
    if (!r.configured) toast('ההתראות עוד לא הוגדרו בשרת', 'error');
    else if (!r.sent) toast('ההתראה לא נמסרה — נסו לכבות ולהדליק את הפעמון', 'error');
    else toast(r.failed ? `נשלחה ל-${r.sent} מכשירים; ${r.failed} נכשלו` : 'נשלחה התראת בדיקה');
  });

  const enable = async () => {
    const sup = pushSupport();
    setSupport(sup);
    if (sup !== 'ok') { toast(SUPPORT_HINT[sup], 'error'); return; }
    if (!state?.vapidPublicKey) { toast('ההתראות עוד לא הוגדרו בשרת', 'error'); return; }
    // subscribe() asks for permission first thing, while still inside the tap (iOS requires it)
    await run(async () => {
      const sub = await subscribe(state.vapidPublicKey!);
      put(await api.addDevice(sub));
      setEndpoint(sub.endpoint ?? null);
      // Prove the whole path works end to end: a real push to this device, through its push service.
      const r = await api.testNotification();
      put(r.state);
      toast(r.sent ? 'ההתראות הופעלו — נשלחה התראת בדיקה' : 'המכשיר נרשם, אבל התראת הבדיקה לא נמסרה', r.sent ? 'info' : 'error');
    });
  };

  const disable = () => run(async () => {
    const ep = await unsubscribe();
    if (ep) put(await api.removeDevice(ep));
    setEndpoint(null);
    toast('ההתראות כובו במכשיר הזה');
  });

  return {
    state, on, support, busy, endpoint, test,
    toggle: () => (on ? disable() : enable()),
    setMode: (mode: NotifyMode) => run(async () => { put(await api.setNotifyMode(mode)); }),
    setChoices: (c: { groups?: string[]; tasks?: boolean }) => run(async () => { put(await api.setNotifyChoices(c)); }),
    removeDevice: (ep: string) => run(async () => { put(await api.removeDevice(ep)); if (ep === endpoint) { await unsubscribe(); setEndpoint(null); } }),
  };
}

/** Header: the bell (pressed = notifications on for this device) and when they may arrive. */
export function NotifyControls({ light }: { light?: boolean }) {
  const n = useNotifications();
  const bell = light
    ? n.on ? 'bg-white text-accent' : 'text-white/90 hover:bg-white/15'
    : n.on ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-page';
  const title = n.on ? 'ההתראות פועלות במכשיר הזה — לחצו לכיבוי' : n.support === 'ok' ? 'הפעלת התראות במכשיר הזה' : SUPPORT_HINT[n.support];
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <button type="button" aria-pressed={n.on} aria-label="התראות" title={title} onClick={n.toggle} disabled={n.busy}
        className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${bell}`}>
        {n.busy ? <Spinner /> : n.on ? <Icons.BellOn size={17} /> : n.support === 'ok' ? <Icons.Bell size={17} /> : <Icons.BellOff size={17} />}
      </button>
      <ModeToggle mode={n.state?.mode ?? 'business'} onChange={n.setMode} light={light} dim={!n.on} />
    </span>
  );
}

function ModeToggle({ mode, onChange, light, dim }: { mode: NotifyMode; onChange: (m: NotifyMode) => void; light?: boolean; dim?: boolean }) {
  return (
    <span role="radiogroup" aria-label="מתי לקבל התראות" title="מחוץ לשעות הפעילות ההתראות נאספות ונשלחות כסיכום כשהעסק נפתח"
      className={`flex rounded-full p-0.5 text-[11px] font-medium ${light ? 'bg-white/15' : 'border border-line bg-page'} ${dim ? 'opacity-60' : ''}`}>
      {(Object.keys(NOTIFY_MODE_LABELS) as NotifyMode[]).map((m) => {
        const active = m === mode;
        const cls = light ? (active ? 'bg-white text-accent' : 'text-white/85') : active ? 'bg-panel text-accent shadow-sm' : 'text-ink-2';
        return <button key={m} type="button" role="radio" aria-checked={active} onClick={() => !active && onChange(m)} className={`whitespace-nowrap rounded-full px-2 py-0.5 ${cls}`}>{NOTIFY_MODE_LABELS[m]}</button>;
      })}
    </span>
  );
}

/** הגדרות › התראות: this device, when, which groups and my tasks (the member's own choice), my devices, and a test. */
export function NotifySettings() {
  const n = useNotifications();
  const s = n.state;
  return (
    <section className="card grid gap-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">התראות</h2>
        <button type="button" className={`btn btn-sm ${n.on ? '' : 'btn-primary'}`} onClick={n.toggle} disabled={n.busy}>
          {n.on ? <><Icons.BellOff size={16} /> כבה במכשיר הזה</> : <><Icons.Bell size={16} /> הפעל במכשיר הזה</>}
        </button>
      </div>
      {n.support !== 'ok' && !n.on && <p className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-sm">{SUPPORT_HINT[n.support]}</p>}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-3">מתי:</span>
        <ModeToggle mode={s?.mode ?? 'business'} onChange={n.setMode} />
        <span className="text-xs text-ink-3">{s?.mode === 'always' ? 'כל ליד, בכל שעה.' : 'מחוץ לשעות הפעילות ההתראות נאספות ונשלחות כסיכום אחד כשהעסק נפתח.'}</span>
      </div>
      <div className="grid gap-1.5 text-sm">
        <span className="text-ink-3">לקבל התראות על:</span>
        {!s ? '…' : (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={s.tasks} disabled={n.busy} onChange={(e) => n.setChoices({ tasks: e.target.checked })} />מועד של משימה שלי</label>
            {s.groups.map((g) => (
              <label key={g.id} className="flex items-center gap-1.5"><input type="checkbox" checked={g.on} disabled={n.busy}
                onChange={(e) => n.setChoices({ groups: s.groups.filter((x) => (x.id === g.id ? e.target.checked : x.on)).map((x) => x.id) })} />ליד חדש ב{g.name}</label>
            ))}
          </div>
        )}
        <span className="text-xs text-ink-3">ההגדרות האלה שלך בלבד. תזכורת משימה נשלחת במועד שלה, בכל שעה.</span>
      </div>
      <div>
        <div className="mb-1 text-sm text-ink-3">המכשירים שלי</div>
        {!s?.devices.length ? <p className="text-sm text-ink-3">אין מכשירים רשומים.</p> : (
          <ul className="grid gap-1.5">
            {s.devices.map((d) => (
              <li key={d.endpoint} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-lg border border-line px-3 py-2 text-sm">
                <span className="font-medium">{d.device ?? 'מכשיר'}</span>
                {d.endpoint === n.endpoint && <span className="rounded bg-accent-soft px-1.5 text-[11px] text-accent">המכשיר הזה</span>}
                <span className="text-xs text-ink-3">
                  {d.lastError ? <span className="text-danger">המסירה האחרונה נכשלה</span>
                    : d.lastSentAt ? <>נמסרה התראה <span className="ltr">{formatDateTime(d.lastSentAt)}</span></> : 'טרם נשלחה התראה'}
                </span>
                <button type="button" className="ms-auto text-xs text-danger" onClick={() => n.removeDevice(d.endpoint)} disabled={n.busy}>הסר</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div><button type="button" className="btn btn-sm" onClick={n.test} disabled={n.busy || !s?.devices.length}>שלח התראת בדיקה</button></div>
    </section>
  );
}
